// Client for reliability settings (time limits, retries, model fallback, hedging, model
// choice) on functions and chains. Backend: api/core/routers/reliability.py, schema in
// api/common/reliability.py.

import { ApiError, TargetType, TokenGetter } from './learningApi';

export type OnFailure = 'standard' | 'fail' | 'retry' | 'fallback';
export type TimeLimitMode = 'off' | 'fixed' | 'auto';
export type TimeLimitPreset = 'relaxed' | 'balanced' | 'strict';
export type FallbackSource = 'auto' | 'same_provider' | 'custom';
export type Percentile = 'p90' | 'p95' | 'p99';
export type HealthState = 'healthy' | 'degraded' | 'down';
export type ModelChoiceMode = 'default' | 'spread';

export interface ReliabilitySettings {
  time_limit: {
    mode: TimeLimitMode;
    seconds: number | null;
    preset: TimeLimitPreset;
    sample_size: number;
    min_seconds: number;
    max_seconds: number | null;
  };
  on_failure: OnFailure;
  max_retries: number;
  fallback_source: FallbackSource;
  fallback_models: string[];
  avoid_degraded: boolean;
  hedge: { enabled: boolean; after: Percentile };
  apply_to_steps: boolean;
  /** spread: each call goes to a random model from `models` (chains: empty, picked per step). */
  model_choice: { mode: ModelChoiceMode; models: string[] };
}

export interface ModelSpeed {
  n: number;
  p50_s?: number | null;
  p90_s?: number | null;
}

export interface DurationStats {
  n: number;
  p50?: number;
  p90?: number;
  p95?: number;
  p99?: number;
  max?: number;
  computed_at?: string;
}

export interface ModelHealth {
  state: HealthState;
  reason: string;
  calls: number;
  failures: Record<string, number>;
  avg_latency_s: number | null;
}

export interface ModelOption {
  name: string;
  provider: string;
  price: number | null;
  vision: boolean;
}

export interface ReliabilityDescription {
  target: { type: TargetType; id: string; name: string };
  saved: boolean;
  is_standard: boolean;
  settings: ReliabilitySettings;
  defaults: ReliabilitySettings;
  stats: DurationStats;
  effective: { time_limit_s: number | null; time_limit_note: string; hedge_after_s: number | null };
  limits: {
    ceiling_s: number;
    min_samples: number;
    max_retries: number;
    presets: Record<TimeLimitPreset, { percentile: Percentile; multiplier: number }>;
  };
  updated_at: string | null;
  updated_by: string | null;
  models?: { primary: string | null; allowed: string[]; needs_vision?: boolean };
  model_options?: ModelOption[];
  model_choice?: { available: boolean; suggested: string[]; speeds: Record<string, ModelSpeed> };
  plan_preview?: { models: string[]; note: string | null };
  health?: Record<string, ModelHealth>;
}

/** Attempt trace stored on a run (`metadata.llm_response.reliability` / `metadata.reliability`). */
export interface ReliabilityTrace {
  mode: OnFailure;
  source: 'function' | 'chain';
  time_limit_s: number | null;
  time_limit_note: string;
  plan: string[];
  note?: string;
  hedged_after_s?: number;
  attempts: { model: string; outcome: string; seconds: number; error?: string; hedge?: boolean; fixed?: boolean }[];
  result: 'ok' | 'failed' | 'time_limit';
  model_used?: string;
  elapsed_s?: number;
  health?: Record<string, HealthState>;
}

export function reliabilityApi(getToken: TokenGetter, actAsPublic = false) {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/reliability${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(actAsPublic ? { 'X-Act-As-Public': 'true' } : {}),
      },
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    get: (type: TargetType, id: string) => request<ReliabilityDescription>(`/${type}/${id}`),
    save: (type: TargetType, id: string, settings: ReliabilitySettings) =>
      request<ReliabilityDescription>(`/${type}/${id}`, { method: 'PUT', body: JSON.stringify({ settings }) }),
    reset: (type: TargetType, id: string) => request<ReliabilityDescription>(`/${type}/${id}`, { method: 'DELETE' }),
    modelHealth: (models?: string[]) =>
      request<{ models: Record<string, ModelHealth> }>(`/models/health${models?.length ? `?models=${encodeURIComponent(models.join(','))}` : ''}`),
  };
}
